import React, { useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { getAllStation } from "../../slices/stations/thunk";

const CHIP_COLORS = {
  green: { bg: "#e6f4ea", color: "#2e7d32" },
  amber: { bg: "#fdf3d8", color: "#9a6a12" },
  blue: { bg: "#e3edfd", color: "#2a5bd7" },
  red: { bg: "#fde8e8", color: "#c62828" },
  grey: { bg: "#eceef1", color: "#555b66" },
};

const STATUS_TONE = {
  Available: "green",
  Occupied: "amber",
  Unavailable: "grey",
  Offline: "grey",
  Faulted: "red",
  Reserved: "blue",
};

const AVAILABILITY_TONE = {
  Available: "green",
  Occupied: "amber",
  Unavailable: "grey",
  Offline: "grey",
  Faulted: "red",
  Reserved: "blue",
};

const AVAILABILITY_LABEL = {
  Available: "Available",
  Occupied: "Occupied",
  Unavailable: "Unavailable",
  Offline: "Offline",
  Faulted: "Faulted",
  Reserved: "Reserved",
};

const StatusChip = ({ label, tone }) => {
  if (!label) return <span style={{ color: "#9aa0a6" }}>—</span>;
  const colors = CHIP_COLORS[tone] || CHIP_COLORS.grey;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.bg,
        color: colors.color,
        borderRadius: "999px",
        padding: "4px 14px",
        fontSize: "0.85rem",
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

const dash = (value) =>
  value ? value : <span style={{ color: "#9aa0a6" }}>—</span>;

function EvseList(props) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllStation());
  }, [dispatch]);

  const responses = useSelector((state) => state.Stations.station);

  const data = useMemo(() => {
    const stationList = Array.isArray(responses)
      ? responses
      : Array.isArray(responses?.data)
        ? responses.data
        : Array.isArray(responses?.stations)
          ? responses.stations
          : responses?.stationDetails
            ? [responses]
            : [];

    const rows = [];

    stationList.forEach((item) => {
      const station = item?.stationDetails ?? item;

      if (
        props?.stationID != null &&
        String(station?.stationID) !== String(props.stationID)
      ) {
        return;
      }

      const connectors = [
        ...(Array.isArray(station?.connectors) ? station.connectors : []),
        // ...(Array.isArray(station?.evse)
        //   ? station.evse.flatMap((evse) => evse?.connectors ?? [])
        //   : []),
      ];

      connectors.forEach((connector) => {
        const rawStatus =
          connector.connectorStatus ?? connector.currentStatus ?? "";

        const normalizedStatus = String(rawStatus).toLowerCase();

        const statusMap = {
          available: "Available",
          occupied: "Occupied",
          unavailable: "Unavailable",
          offline: "Offline",
          faulted: "Faulted",
          reserved: "Reserved",
        };

        const currentStatus = statusMap[normalizedStatus] ?? "";

        rows.push({
          connectorId: connector.connectorId ?? "",
          connectorType:
            connector.connectorType ?? connector.connectorName ?? "",
          powerKw:
            connector.powerKw ?? connector.power ?? connector.maxPower ?? "",
          currentStatus,
          availability: AVAILABILITY_LABEL[currentStatus] ?? "",
          activeSession: connector.activeSession ?? connector.sessionId ?? "",
          errorCode: connector.errorCode ?? "",
        });
      });
    });

    return rows;
  }, [responses, props?.stationID]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "connectorId",
        header: "Connector ID",
        Cell: ({ cell }) => dash(cell.getValue()),
      },
      {
        accessorKey: "connectorType",
        header: "Connector Type",
        Cell: ({ cell }) => dash(cell.getValue()),
      },
      {
        accessorKey: "powerKw",
        header: "Power",
        Cell: ({ cell }) => {
          const v = cell.getValue();
          if (v === undefined || v === null || v === "") return dash("");
          return /kw/i.test(String(v)) ? v : `${v} kW`;
        },
      },
      {
        accessorKey: "availability",
        header: "Availability",
        Cell: ({ cell, row }) => (
          <StatusChip
            label={cell.getValue()}
            tone={AVAILABILITY_TONE[row.original.currentStatus] || "grey"}
          />
        ),
      },
      {
        accessorKey: "currentStatus",
        header: "Current Status",
        Cell: ({ cell }) => (
          <StatusChip
            label={cell.getValue()}
            tone={STATUS_TONE[cell.getValue()] || "grey"}
          />
        ),
      },
      {
        accessorKey: "activeSession",
        header: "Active Session",
        Cell: ({ cell }) => dash(cell.getValue()),
      },
      {
        accessorKey: "errorCode",
        header: "Error Code",
        Cell: ({ cell }) => {
          const v = cell.getValue();
          if (!v) return dash("");
          return (
            <span style={{ color: v === "NoError" ? "#6b7280" : "inherit" }}>
              {v}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="table-responsive border mt-4">
      <MaterialReactTable
        columns={columns}
        data={data}
        enableTopToolbar={false}
        enableColumnActions={false}
        enableSorting
        enableColumnFilters={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        initialState={{ pagination: { pageSize: 10, pageIndex: 0 } }}
        muiPaginationProps={{
          rowsPerPageOptions: [5, 10, 25, 50],
          showFirstButton: false,
          showLastButton: false,
        }}
        muiTablePaperProps={{
          elevation: 0,
          sx: { borderRadius: "8px", fontFamily: "Inter, sans-serif" },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontFamily: "Inter, sans-serif",
            fontWeight: 700,
            fontSize: "0.95rem",
            color: "#1f2328",
            backgroundColor: "#fafafa",
            padding: "18px 16px",
            borderBottom: "1px solid #e6e8eb",
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontFamily: "Inter, sans-serif",
            fontSize: "0.95rem",
            color: "#1f2328",
            padding: "18px 16px",
            borderBottom: "1px solid #eceef1",
          },
        }}
        muiTableBodyRowProps={{
          sx: { backgroundColor: "#fff" },
        }}
        muiBottomToolbarProps={{
          sx: { fontFamily: "Inter, sans-serif", backgroundColor: "#fff" },
        }}
      />
    </div>
  );
}

export default EvseList;
